/**
 * Phase 1.7 — single Card primitive.
 *
 * One Card. Variants are composition (slot props), not new components.
 * Tend's chrome treats every "panel" — KPI tile, chart wrapper, list row
 * group — as the same shape: surface bg, 1px border, 12px radius, 16/24px
 * pad. Subclasses are forbidden by convention so divergence stays a
 * deliberate design call rather than an accident.
 *
 * Slots:
 *   - thumbnail       optional square block (asset card grid)
 *   - title           required (or pass `header` to override)
 *   - subtitle        optional, sits under title
 *   - badge           optional, top-right StatusBadge slot
 *   - actions         optional, top-right action row
 *   - footer          optional, bottom border-separated row
 *
 * `interactive` flips on the hover treatment + focus ring + cursor for
 * link-card use cases. `compact` collapses padding for KPI strips.
 */

import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface CardProps {
  thumbnail?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  header?: ReactNode;
  children?: ReactNode;
  interactive?: boolean;
  compact?: boolean;
  className?: string;
  bodyClassName?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  props,
  ref,
) {
  const {
    thumbnail,
    title,
    subtitle,
    badge,
    actions,
    footer,
    header,
    children,
    interactive,
    compact,
    className,
    bodyClassName,
    onClick,
    ariaLabel,
  } = props;

  const hasHeader = Boolean(header || title || subtitle || badge || actions);

  return (
    <div
      ref={ref}
      onClick={onClick}
      role={interactive && onClick ? 'button' : undefined}
      aria-label={ariaLabel}
      tabIndex={interactive && onClick ? 0 : undefined}
      onKeyDown={
        interactive && onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        'rounded-xl border border-border bg-surface text-charcoal shadow-sm transition-colors',
        'dark:bg-surface-dark dark:text-charcoal-dark dark:border-border',
        interactive &&
          'cursor-pointer hover:bg-surface-raised dark:hover:bg-surface-raised-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-burnt-orange dark:focus-visible:ring-burnt-orange-dark',
        className,
      )}
    >
      {thumbnail ? <div className="overflow-hidden rounded-t-xl">{thumbnail}</div> : null}
      <div className={cn(compact ? 'p-4' : 'p-5', bodyClassName)}>
        {hasHeader ? (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {header ?? (
                <>
                  {title ? (
                    <div className="font-display text-lg leading-tight text-charcoal dark:text-charcoal-dark">
                      {title}
                    </div>
                  ) : null}
                  {subtitle ? (
                    <div className="mt-1 text-sm text-muted-foreground">{subtitle}</div>
                  ) : null}
                </>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {badge}
              {actions}
            </div>
          </div>
        ) : null}
        {children ? <div className={cn(hasHeader && 'mt-4')}>{children}</div> : null}
      </div>
      {footer ? (
        <div className="border-t border-border px-5 py-3 text-sm text-muted-foreground dark:border-border">
          {footer}
        </div>
      ) : null}
    </div>
  );
});

/**
 * Brand-mark thumbnail fallback. Use when a card slot expects a thumbnail
 * but there's no R2 image. The container is `bg-sand` in light and
 * `bg-surface-raised` in dark, with the E2 H mark stroked in Forest at
 * roughly 60% of container width.
 */
export function CardThumbnailFallback({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn(
        'flex aspect-[4/3] w-full items-center justify-center bg-sand dark:bg-surface-raised-dark',
        className,
      )}
      aria-hidden="true"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 64"
        className="h-3/5 w-3/5 text-forest dark:text-forest-dark"
      >
        <rect
          x="1.75"
          y="1.75"
          width="60.5"
          height="60.5"
          rx="6"
          ry="6"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
        />
        <g fill="currentColor">
          <rect x="18.5" y="18" width="6" height="28" />
          <rect x="39.5" y="18" width="6" height="28" />
          <rect x="18.5" y="29" width="27" height="6" />
        </g>
      </svg>
    </div>
  );
}
